import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import SprNumberPriceCard from "./spr-number-price-card";
import "./spr-number-price.css";

const DRAWER_WIDTH_KEY = "planOnline.sprNumberPrice.zagDrawer.width";
const MIN_WIDTH = 420;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 480;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer шапки прайс-листа.
 */
const SprNumberPrice = ({ open, idNumberPrice, isNew = false, onClose, onSaved }) => {
  const [size, setSize] = useState(loadWidth);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const title = isNew
    ? "Новый прайс-лист"
    : `Прайс-лист${idNumberPrice != null ? ` №${idNumberPrice}` : ""}`;

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={maxAllowedWidth()}
      open={open}
      onClose={onClose}
      destroyOnClose
      closable={false}
      rootClassName="spr-number-price-drawer"
      extra={
        <Button
          type="text"
          className="spr-number-price-drawer__close"
          icon={<CloseOutlined />}
          aria-label="Закрыть"
          title="Закрыть"
          onClick={onClose}
        />
      }
      styles={{
        body: { display: "flex", flexDirection: "column", paddingTop: 12 }
      }}
      resizable={{
        onResize,
        onResizeEnd
      }}
    >
      {open && (idNumberPrice != null || isNew) ? (
        <SprNumberPriceCard
          idNumberPrice={idNumberPrice}
          isNew={isNew}
          open={open}
          onSaved={onSaved}
          onCancel={onClose}
        />
      ) : null}
    </Drawer>
  );
};

export default SprNumberPrice;
